// src/pages/sow-analysis/AnalysisCharts.jsx
//
// Chart building blocks for AnalysisDetailModal. A SOW drafter reading this
// modal cares about "how much is missing, and where" at a glance -- these
// give that view; the tables next to each chart still carry the row-level
// detail (rule names included) for when someone wants to drill in.

import { useMemo } from "react";
import PropTypes from "prop-types";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { severityLabel } from "./sowAnalysisHelpers";
import { COVERAGE_COLORS, SEVERITY_COLORS } from "../../utils/constants";

function EmptyChart({ label }) {
  return <div className="flex items-center justify-center h-[140px] text-xs text-muted">{label}</div>;
}
EmptyChart.propTypes = { label: PropTypes.string.isRequired };

/** Dims every cell except the selected one, so a chart-click or dropdown filter is visible on the chart itself. */
function cellOpacity(name, selected) {
  return !selected || selected === name ? 1 : 0.35;
}

/**
 * Donut of rule coverage -- Covered / Partial / Missing. Fixed pixel size
 * rather than ResponsiveContainer: this chart is always placed beside a
 * legend in a flex row, and ResponsiveContainer's width-from-parent
 * measurement raced that flex layout and clipped the circle against a
 * stale, too-narrow width. A fixed size sidesteps the race entirely.
 */
export function RuleCoveragePieChart({ covered, partial, missing, selected, onSelect }) {
  const data = useMemo(
    () =>
      [
        { name: "Covered", value: covered },
        { name: "Partial", value: partial },
        { name: "Missing", value: missing },
      ].filter((d) => d.value > 0),
    [covered, partial, missing]
  );

  if (data.length === 0) return <EmptyChart label="No rule coverage data." />;

  return (
    <PieChart width={150} height={150}>
      <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={68} paddingAngle={2}>
        {data.map((d) => (
          <Cell
            key={d.name}
            fill={COVERAGE_COLORS[d.name]}
            fillOpacity={cellOpacity(d.name, selected)}
            onClick={() => onSelect?.(d.name)}
            style={{ cursor: onSelect ? "pointer" : undefined }}
          />
        ))}
      </Pie>
      <Tooltip formatter={(value, name) => [`${value} rule${value === 1 ? "" : "s"}`, name]} />
    </PieChart>
  );
}
RuleCoveragePieChart.propTypes = {
  covered: PropTypes.number.isRequired,
  partial: PropTypes.number.isRequired,
  missing: PropTypes.number.isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
};

/** Horizontal bar of issue counts by severity. */
export function IssuesSeverityChart({ issues, selected, onSelect }) {
  const data = useMemo(() => {
    const counts = new Map();
    for (const issue of issues) {
      const label = severityLabel(issue.severity);
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    const order = ["Critical", "High", "Medium", "Low", "Unknown"];
    return order.filter((k) => counts.has(k)).map((k) => ({ name: k, value: counts.get(k) }));
  }, [issues]);

  if (data.length === 0) return <EmptyChart label="No issues to chart." />;

  return (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={60} />
        <Tooltip formatter={(value) => [`${value} issue${value === 1 ? "" : "s"}`, "Count"]} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} onClick={(entry) => onSelect?.(entry?.payload?.name)}>
          {data.map((d) => (
            <Cell
              key={d.name}
              fill={SEVERITY_COLORS[d.name] || SEVERITY_COLORS.Unknown}
              fillOpacity={cellOpacity(d.name, selected)}
              style={{ cursor: onSelect ? "pointer" : undefined }}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
IssuesSeverityChart.propTypes = { issues: PropTypes.array.isRequired, selected: PropTypes.string, onSelect: PropTypes.func };

/** Horizontal bar of average rule score per engine category. */
export function CategoryScoreChart({ ruleEvals, selected, onSelect }) {
  const data = useMemo(() => {
    const byCategory = new Map();
    for (const r of ruleEvals) {
      if (typeof r.rule_score !== "number") continue;
      const category = r.engine_category || "Uncategorized";
      const entry = byCategory.get(category) || { total: 0, count: 0 };
      entry.total += r.rule_score;
      entry.count += 1;
      byCategory.set(category, entry);
    }
    return Array.from(byCategory.entries())
      .map(([name, { total, count }]) => ({ name, value: Math.round((total / count) * 10) / 10 }))
      .sort((a, b) => a.value - b.value);
  }, [ruleEvals]);

  if (data.length === 0) return <EmptyChart label="No per-rule scores to chart." />;

  return (
    <ResponsiveContainer width="100%" height={Math.max(140, data.length * 34)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
        <Tooltip formatter={(value) => [value, "Avg. score"]} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} onClick={(entry) => onSelect?.(entry?.payload?.name)}>
          {data.map((d) => (
            <Cell key={d.name} fill="#3b82f6" fillOpacity={cellOpacity(d.name, selected)} style={{ cursor: onSelect ? "pointer" : undefined }} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
CategoryScoreChart.propTypes = { ruleEvals: PropTypes.array.isRequired, selected: PropTypes.string, onSelect: PropTypes.func };
